import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, Input, Select, Switch, Tabs, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { CallButton } from '@/phone/CallButton';
import { dateTime, money, relativeTime } from '@/lib/format';
import { addProspectNote, convertProspect, getMembers, getProspect, getProspectSummary, logProspectCall, setProspectStatus, updateProspect } from '@/data/client';
import type { CallOutcome, ClosedReason, Prospect, ProspectSummary, TouchChannel } from '@/data/types';
import { FormError, Modal } from '@/components/crm/Modal';
import { attemptsLabel, CHANNELS, CLOSE_REASONS, HISTORY_ICON, OUTCOMES, ProspectStatusBadge } from '@/components/prospecting/ProspectBits';
import { BookDiscoveryModal } from '@/components/prospecting/BookDiscovery';
import { scoreBand } from './Prospecting';

/* One prospect: why to call (the research), the call log, and the decisions —
   log a call, convert to a lead, mark cold, close, or put back in rotation. */
export function ProspectDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['prospect', id], queryFn: () => getProspect(id) });
  const { data: members = [] } = useQuery({ queryKey: ['members'], queryFn: getMembers, staleTime: 10 * 60_000 });
  const [modal, setModal] = useState<'book' | 'convert' | 'close' | null>(null);
  const refresh = () => { for (const k of ['prospect', 'prospects', 'prospectSummary']) void qc.invalidateQueries({ queryKey: [k] }); };
  const assign = useMutation({ mutationFn: (ownerUserId: string) => updateProspect(id, { ownerUserId: ownerUserId || null }), onSuccess: refresh });
  const status = useMutation({ mutationFn: (s: 'active' | 'cold') => setProspectStatus(id, { status: s }), onSuccess: refresh });
  const { data: summary } = useQuery({ queryKey: ['prospectSummary'], queryFn: getProspectSummary, staleTime: 5 * 60_000 });
  const schedule = useMutation({ mutationFn: (iso: string) => updateProspect(id, { nextAttemptAt: iso }), onSuccess: refresh });

  if (isLoading) return null;
  if (!data) return <div className="dash-empty"><h2>Prospect not found</h2><Link to="/prospecting">Back to prospecting</Link></div>;
  const { prospect: p, history } = data;
  const inRotation = p.status === 'new' || p.status === 'active';

  return (
    <div className="crm-record">
      <Link to="/prospecting" className="cq-back"><Icon name="arrow-left" size={16} /> Prospecting</Link>
      <div className="crm-record__head">
        <div className="crm-record__title">
          <h2>{p.name} <ProspectStatusBadge p={p} />{p.timesFound > 1 && <Badge variant="outline">Found {p.timesFound}×</Badge>}</h2>
          <div className="crm-record__sub">
            {p.phone && <CallButton to={p.phone} name={p.name} />}
            {p.website && <a href={p.website} target="_blank" rel="noreferrer"><Icon name="globe" size={14} /> {p.domain}</a>}
            {p.mapsUrl && <a href={p.mapsUrl} target="_blank" rel="noreferrer"><Icon name="map-pin" size={14} /> Google Maps</a>}
            {(p.city || p.state) && <span>{[p.city, p.state].filter(Boolean).join(', ')}</span>}
            {p.category && <span>{p.category}</span>}
          </div>
        </div>
        <div className="crm-record__actions">
          <select className="crm-inline-select" aria-label="Owner" value={p.ownerUserId ?? ''} onChange={(e) => assign.mutate(e.target.value)}>
            <option value="">Unassigned</option>
            {members.map((m) => <option key={m.userId} value={m.userId}>{m.name ?? m.email}</option>)}
          </select>
          {p.status !== 'converted' && <Button size="sm" iconLeft={<Icon name="calendar" size={14} />} onClick={() => setModal('book')}>Book discovery call</Button>}
          {p.status !== 'converted' && <Button size="sm" variant="outline" onClick={() => setModal('convert')} title="Interested, but no call booked yet">Lead without a call</Button>}
          {inRotation && <Button size="sm" variant="outline" onClick={() => status.mutate('cold')}>Mark cold</Button>}
          {inRotation && <Button size="sm" variant="ghost" onClick={() => setModal('close')}>Close</Button>}
          {(p.status === 'cold' || p.status === 'closed') && <Button size="sm" variant="outline" iconLeft={<Icon name="repeat" size={14} />} onClick={() => status.mutate('active')}>Back in rotation</Button>}
        </div>
      </div>
      <FormError error={assign.error ?? status.error} />
      {p.status === 'converted' && p.companyId && (
        <p className="crm-callout">Became a lead {p.convertedAt ? relativeTime(p.convertedAt) : ''}. Its calls and notes are on <Link to={`/companies/${p.companyId}`}>the company's timeline</Link>.</p>
      )}
      {p.crmNote && <p className="crm-callout">{p.crmNote}</p>}

      <div className="crm-record__cols">
        <div className="crm-list">
          {inRotation && <CallLogger p={p} onDone={refresh} />}
          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Notes, calls and texts</h3><span className="crm-cell-sub">{attemptsLabel(p.callCount)}</span></div>
            <NoteBox id={id} onDone={refresh} />
            {history.length ? (
              <ol className="crm-timeline">
                {history.map((h) => (
                  <li key={h.id} className={`crm-timeline__item crm-timeline__item--${h.type}`}>
                    <span className="crm-timeline__icon"><Icon name={HISTORY_ICON[h.type] ?? 'refresh-cw'} size={14} /></span>
                    <div className="crm-timeline__main">
                      <div className="crm-timeline__meta">
                        <strong>{h.userName ?? (h.type === 'note' ? 'Imported note' : 'System')}</strong>
                        <span title={dateTime(h.occurredAt)}>{relativeTime(h.occurredAt)}</span>
                      </div>
                      {h.body && <p className={h.type === 'note' ? 'crm-timeline__note' : 'crm-timeline__body'}>{h.body}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            ) : <p className="lead-none">No calls or texts yet.</p>}
          </Card>
        </div>
        <div className="crm-record__side">
          <Research p={p} />
          <Card padding={false} className="crm-section">
            <div className="crm-section__head"><h3>Rotation</h3></div>
            <dl className="crm-fields">
              <dt>Attempts</dt><dd>{p.callCount}{p.cycleAttempts && p.status === 'active' ? ` (${p.cycleAttempts} unanswered this round)` : ''}</dd>
              <dt>Last attempt</dt><dd>{p.lastCallAt ? dateTime(p.lastCallAt) : '—'}</dd>
              <dt>Next attempt</dt>
              <dd>{inRotation ? <NextAttempt p={p} onPick={(iso) => schedule.mutate(iso)} busy={schedule.isPending} error={schedule.error} /> : '—'}</dd>
              <dt>{p.origin === 'manual' ? 'Added' : 'First found'}</dt>
              <dd>{dateTime(p.foundAt)}{p.origin === 'sheet' ? ' (from the sheet)' : p.origin === 'manual' ? ' (by hand)' : ''}</dd>
              {p.timesFound > 1 && <><dt>Found again</dt><dd>{dateTime(p.lastFoundAt)}</dd></>}
            </dl>
            {summary && <p className="pq-rule">{cadenceRule(summary)}</p>}
          </Card>
        </div>
      </div>

      {modal === 'book' && <BookDiscoveryModal p={p} onClose={() => setModal(null)} />}
      {modal === 'convert' && <ConvertModal p={p} onClose={() => setModal(null)} />}
      {modal === 'close' && <CloseModal p={p} onClose={() => setModal(null)} onDone={refresh} />}
    </div>
  );
}

/* The rotation in one sentence, from the workspace's cadence settings, so a rep
   never has to guess when the next attempt lands. */
function cadenceRule(s: ProspectSummary): string {
  const { attempts, windowDays } = s.cadence;
  const gap = attempts > 1 ? windowDays / (attempts - 1) : windowDays;
  const gapText = gap >= 1 ? `${Number.isInteger(gap) ? gap : gap.toFixed(1)} day${gap === 1 ? '' : 's'}` : `${Math.round(gap * 24)} hours`;
  const to12 = (h: number) => `${((h + 11) % 12) + 1}${h < 12 ? 'am' : 'pm'}`;
  return `${attempts} attempts over ${windowDays} days: each unanswered call, text or email schedules the next one ${gapText} later, `
    + `on a weekday between ${to12(s.hours.startHour)} and ${to12(s.hours.endHour)}. A call back or a reply starts the count over. `
    + `After ${attempts} unanswered, the prospect goes cold.`;
}

/* The next attempt, editable in place: "Due now" or the scheduled time, quick
   picks (9am local on the next working days), or any date and time. */
function NextAttempt({ p, onPick, busy, error }: { p: Prospect; onPick: (iso: string) => void; busy: boolean; error: unknown }) {
  const [custom, setCustom] = useState('');
  const due = !p.nextAttemptAt || Date.parse(p.nextAttemptAt) <= Date.now();
  const picks = [
    { label: 'In 2 hours', at: () => new Date(Date.now() + 2 * 3_600_000) },
    { label: nextWorkingDay(1).label, at: () => nextWorkingDay(1).date },
    { label: nextWorkingDay(3).label, at: () => nextWorkingDay(3).date },
    { label: 'Next week', at: () => nextWorkingDay(7).date },
  ];
  return (
    <div className="pq-next">
      <span>{due ? <span className="pq-due">Due now</span> : <>{dateTime(p.nextAttemptAt!)} <span className="crm-cell-sub">({relativeTime(p.nextAttemptAt!)})</span></>}</span>
      <div className="pq-next__picks" role="group" aria-label="Move the next attempt">
        {picks.map((k) => <button key={k.label} type="button" className="pq-outcome" disabled={busy} onClick={() => onPick(k.at().toISOString())}>{k.label}</button>)}
      </div>
      <div className="pq-next__custom">
        <Input label="Or pick a time" type="datetime-local" value={custom} onChange={(e) => setCustom(e.target.value)} />
        <Button size="sm" variant="outline" disabled={!custom || busy} onClick={() => { onPick(new Date(custom).toISOString()); setCustom(''); }}>Set</Button>
      </div>
      <FormError error={error} />
    </div>
  );
}

/* 9:00 local, `days` ahead, rolled forward off a weekend; labelled by weekday
   so Friday's "tomorrow" reads "Mon 9am", not "tomorrow". */
function nextWorkingDay(days: number): { date: Date; label: string } {
  const d = new Date();
  d.setDate(d.getDate() + days);
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return { date: d, label: `${d.toLocaleDateString('en-US', { weekday: 'short' })} 9am` };
}

/* Channel first (call, text, email), then the outcome (one click), note
   optional, a time for a call back (required) or a reply (optional). Texts and
   emails are ones the rep sent from their own phone or inbox; they move the
   same rotation as calls. */
function CallLogger({ p, onDone }: { p: Prospect; onDone: () => void }) {
  const [channel, setChannel] = useState<TouchChannel>('phone');
  const [outcome, setOutcome] = useState<CallOutcome | null>(null);
  const [note, setNote] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const save = useMutation({
    mutationFn: () => logProspectCall(p.id, { channel, outcome: outcome!, note: note || undefined, callbackAt: callbackAt ? new Date(callbackAt).toISOString() : undefined }),
    onSuccess: () => { setOutcome(null); setNote(''); setCallbackAt(''); onDone(); },
  });
  const label = CHANNELS.find((c) => c.value === channel)!.label;
  const pickChannel = (v: string) => { setChannel(v as TouchChannel); setOutcome(null); setCallbackAt(''); };
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head">
        <h3>Log a call, text or email</h3>
        {p.phone && channel === 'sms' && <a className="pq-call" href={`sms:${p.phone}`}><Icon name="message-square" size={14} /> Text {p.phone}</a>}
        {p.phone && channel !== 'sms' && <CallButton className="pq-call" to={p.phone} label={`Call ${p.phone}`} name={p.name} />}
      </div>
      {p.salesHook && <p className="pq-hookline">{p.salesHook}</p>}
      <div className="pq-channels"><Tabs items={CHANNELS.map((c) => ({ value: c.value, label: c.label }))} value={channel} onChange={pickChannel} /></div>
      <div className="pq-outcomes" role="group" aria-label={channel === 'phone' ? 'How did the call go?' : `What happened with the ${label.toLowerCase()}?`}>
        {OUTCOMES[channel].map((o) => (
          <button key={o.value} type="button" title={o.hint} className={`pq-outcome ${outcome === o.value ? 'is-on' : ''} pq-outcome--${o.value}`}
            onClick={() => setOutcome(o.value)}>{o.label}</button>
        ))}
      </div>
      {outcome && (
        <div className="crm-form" style={{ marginTop: 12 }}>
          {outcome === 'callback' && <Input label="Call back at" type="datetime-local" required value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} />}
          {outcome === 'replied' && <Input label="Follow up at (optional)" type="datetime-local" hint="Leave empty for the normal gap" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} />}
          <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={channel === 'phone' ? 'Who you spoke to, what they said…' : 'What you sent, what they said…'} />
          <div className="crm-record__actions">
            <Button onClick={() => save.mutate()} disabled={save.isPending || (outcome === 'callback' && !callbackAt)}>{save.isPending ? 'Saving…' : `Save ${label.toLowerCase()}`}</Button>
            <Button variant="ghost" onClick={() => setOutcome(null)}>Cancel</Button>
          </div>
          <FormError error={save.error} />
        </div>
      )}
    </Card>
  );
}

function NoteBox({ id, onDone }: { id: string; onDone: () => void }) {
  const [body, setBody] = useState('');
  const save = useMutation({ mutationFn: () => addProspectNote(id, body), onSuccess: () => { setBody(''); onDone(); } });
  return (
    <div className="crm-form pq-notebox">
      <Textarea aria-label="Add a note" rows={2} placeholder="Add a note — who to ask for, best time, what they use now…" value={body} onChange={(e) => setBody(e.target.value)} />
      {body.trim() && <div><Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>Save note</Button></div>}
      <FormError error={save.error} />
    </div>
  );
}

function Research({ p }: { p: Prospect }) {
  const ranked = p.rankings.filter((r) => r.position != null).sort((a, b) => a.position! - b.position!);
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head"><h3>Why call</h3><span className={`pq-score pq-score--${scoreBand(p.score)}`}>{p.score ?? '—'}</span></div>
      <dl className="crm-fields">
        {p.foundBy && <><dt>Found by</dt><dd>Search term “{p.foundBy}”{p.category ? ` · matched to ${p.category}` : ''}</dd></>}
        {p.providerStatus && (
          <><dt>Website by</dt><dd>{p.providerStatus === 'found' ? p.provider : p.providerStatus === 'unreadable' ? 'Site could not be read' : 'No known provider'}</dd></>
        )}
        <dt>Reviews</dt><dd>{p.reviews ?? '—'}{p.rating ? ` · ${p.rating}★` : ''}{p.claimed ? ' · claimed' : ''}</dd>
        <dt>Best organic</dt><dd>{p.bestPosition ? `#${p.bestPosition}` : '—'}{p.page1Keywords ? ` · page 1 for ${p.page1Keywords}` : ''}</dd>
        <dt>Map pack</dt><dd>{p.inMapPack ? 'Yes' : 'No'}</dd>
        <dt>Seen in ads</dt><dd>{p.adKeywords.length ? p.adKeywords.join(', ') : 'Not in live results'}</dd>
        <dt>Ads Transparency</dt><dd>{p.adsTransparency ?? '—'}{p.lastAdSeen ? ` · last ${relativeTime(p.lastAdSeen)}` : ''}</dd>
        <dt>Paid (est.)</dt><dd>{p.paidKeywords != null ? `${p.paidKeywords} keywords · ${money(p.paidValue)}/mo` : '—'}</dd>
        {p.listingCount && p.listingCount > 1 ? <><dt>Listings</dt><dd>{p.listingCount} in the area</dd></> : null}
      </dl>
      {ranked.length > 0 && (
        <table className="pq-ranks">
          <tbody>{p.rankings.map((r) => <tr key={r.keyword}><td>{r.keyword}</td><td className="crm-num">{r.position ? `#${r.position}` : '—'}</td></tr>)}</tbody>
        </table>
      )}
    </Card>
  );
}

function ConvertModal({ p, onClose }: { p: Prospect; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState({ firstName: '', lastName: '', email: '', phone: p.phone ?? '', note: '', createDeal: false, dealName: '', mrr: '' });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  const save = useMutation({
    mutationFn: () => convertProspect(p.id, { ...f, dealName: f.dealName || undefined, mrr: f.mrr || undefined }),
    onSuccess: (r) => {
      for (const k of ['prospect', 'prospects', 'prospectSummary', 'leads', 'leadSummary', 'companies', 'deals']) void qc.invalidateQueries({ queryKey: [k] });
      navigate(r.dealId ? `/deals/${r.dealId}` : `/companies/${r.companyId}`);
    },
  });
  return (
    <Modal title={`${p.name} is interested`} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Creating…' : 'Create lead'}</Button></>}>
      <div className="crm-form">
        <p className="crm-cell-sub">Creates a Cold call lead for {p.name}. The research, calls and notes move to the company's timeline.</p>
        <div className="crm-grid2">
          <Input label="First name" value={f.firstName} onChange={set('firstName')} />
          <Input label="Last name" value={f.lastName} onChange={set('lastName')} />
          <Input label="Email" type="email" value={f.email} onChange={set('email')} />
          <Input label="Phone" value={f.phone} onChange={set('phone')} />
        </div>
        <Textarea label="What did they say?" rows={3} value={f.note} onChange={set('note')} />
        <Switch label="Create a deal now (first pipeline stage)" checked={f.createDeal} onChange={(e) => setF((x) => ({ ...x, createDeal: e.target.checked }))} />
        {f.createDeal && (
          <div className="crm-grid2">
            <Input label="Deal name" placeholder={p.name} value={f.dealName} onChange={set('dealName')} />
            <Input label="Expected MRR" inputMode="decimal" placeholder="$" value={f.mrr} onChange={set('mrr')} />
          </div>
        )}
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}

function CloseModal({ p, onClose, onDone }: { p: Prospect; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState<ClosedReason | ''>('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => setProspectStatus(p.id, { status: 'closed', reason: reason as ClosedReason, note: note || undefined }),
    onSuccess: () => { onDone(); onClose(); },
  });
  return (
    <Modal title={`Close ${p.name}`} onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={!reason || save.isPending}>Close prospect</Button></>}>
      <div className="crm-form">
        <Select label="Why" placeholder="Choose…" options={CLOSE_REASONS} value={reason} onChange={(e) => setReason(e.target.value as ClosedReason)} />
        <Textarea label="Note (optional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        <p className="crm-cell-sub">Closed prospects stay on file: a later search that finds {p.domain} shows this instead of adding it again.{reason === 'do_not_contact' ? ' Do not contact also blocks the domain.' : ''}</p>
        <FormError error={save.error} />
      </div>
    </Modal>
  );
}
